/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormattedDate } from 'react-intl';
import { LocalDate, LocalDateTime, nativeJs } from 'js-joda';
import DayPicker from 'react-day-picker';
import clsx from 'clsx';

import * as time from '@tock/shared/utils/time';
import { CalendarNavBar } from './CalendarNavBar';
import { DayPickerProps, IWithContextProps, withContext } from '@tock/coretypes';
import { DisplayWeekday } from '@tock/time';
import { addOrReplaceDate } from 'utils/multiDateUtils';
import { find } from 'es-toolkit/compat';
import { getBusinessDay } from 'utils/timeUtils';
import BaseComponent from '@tock/shared/components/BaseComponent';
import DefaultCalendarDay from 'components/calendar/DefaultCalendarDay';
import YearMonthForm from 'components/calendar/YearMonthPicker';

export function defaultRenderDay(day: Date, modifiers: any) {
  return <DefaultCalendarDay day={day} modifiers={modifiers} />;
}

export interface Props {
  allowMultipleDates?: boolean;
  disablePastDates?: boolean;
  disableAfterDate?: LocalDate;
  disableFutureDates?: boolean;
  initialDate?: LocalDate | LocalDate[];
  modifierClassName?: string;
  renderDay?(day: Date, modifiers: any): JSX.Element;
  selectedDate?: LocalDate | LocalDate[];
  onSelectDate(date: LocalDate): void;
  /** By default, "today" is just the local date and time. Set this property to `true` if you want the current date & time to take the business day into account. For example, setting this property to `true` will cause January 2 at 2AM to be considered as the January 1 business date. */
  shouldUseBusinessDayAsToday?: boolean;
}

interface State {
  initialMonth: LocalDate;
  selectedDate: LocalDate | LocalDate[];
}

class Calendar extends BaseComponent<Props & IWithContextProps, State> {
  today: LocalDate;
  dayPickerNode: any;

  constructor(props: Props & IWithContextProps) {
    super(props);

    this.today = props.shouldUseBusinessDayAsToday
      ? getBusinessDay(LocalDateTime.now())
      : LocalDate.now();
    const initialMonth =
      Array.isArray(props.initialDate) && props.initialDate
        ? props.initialDate[0]
        : props.initialDate;
    this.state = {
      initialMonth: initialMonth || this.today,
      selectedDate: props.selectedDate || (props.allowMultipleDates ? [this.today] : this.today),
    };
  }

  handleDayClick(dateObj: Date, modifiers: any) {
    const { selectedDate } = this.state;
    const { allowMultipleDates, onSelectDate } = this.props;
    if (modifiers.disabled) {
      return;
    }

    const normalizedDate = LocalDate.from(nativeJs(dateObj));
    onSelectDate(normalizedDate);
    if (!allowMultipleDates || !Array.isArray(selectedDate)) {
      this.setState({
        selectedDate: normalizedDate,
      });
    } else {
      const currentDates = addOrReplaceDate(selectedDate, normalizedDate);
      this.setState({
        selectedDate: currentDates,
      });
    }
  }

  setSelectedDate(selectedDate: LocalDate) {
    this.setState({ selectedDate });
    this.dayPickerNode.showMonth(time.toLocalJSDate(selectedDate));
  }

  isSelected(dateObj: any) {
    const { selectedDate } = this.state;
    const normalizedDate = LocalDate.from(nativeJs(dateObj));
    return Array.isArray(selectedDate)
      ? !!find(selectedDate, (date) => date.equals(normalizedDate))
      : normalizedDate.isEqual(selectedDate);
  }

  handleSetRef(nodeRef: any) {
    this.dayPickerNode = nodeRef;
  }

  disablePastDates(dateObj: Date) {
    const { disablePastDates, disableAfterDate } = this.props;
    const date = LocalDate.from(nativeJs(dateObj));
    const isBeforeToday = disablePastDates && date.isBefore(this.today);
    const isAfterDate = disableAfterDate && date.isAfter(disableAfterDate);
    return isBeforeToday || isAfterDate;
  }

  disableFutureDates(dateObj: Date) {
    const { disableFutureDates } = this.props;
    const date = LocalDate.from(nativeJs(dateObj));
    const isAfterToday = disableFutureDates && date.isAfter(this.today);
    return isAfterToday;
  }

  handleYearMonthChange(initialMonth: Date) {
    this.dayPickerNode.showMonth(initialMonth);
  }

  render() {
    const {
      disableAfterDate,
      disablePastDates,
      context: { locale },
      modifierClassName,
      renderDay,
      disableFutureDates,
    } = this.props;

    const { initialMonth } = this.state;

    const dayPickerProps: DayPickerProps = {
      fixedWeeks: true,
      initialMonth: time.toLocalJSDate(initialMonth),
      localeUtils: {
        formatDay: (dateObj: Date) => {
          return LocalDate.from(nativeJs(dateObj)).toString();
        },
        formatWeekdayShort(i: any) {
          return <DisplayWeekday displayWeekdayAs="short" weekdayValue={i} />;
        },
        formatWeekdayLong(i: any) {
          return <DisplayWeekday displayWeekdayAs="long" weekdayValue={i} />;
        },
        formatMonthTitle: (date: any) => {
          return <FormattedDate locales={[locale]} month="long" value={date} year="numeric" />;
        },
        getFirstDayOfWeek() {
          return 0;
        },
      },
      locale,
      renderDay: renderDay || defaultRenderDay,
      selectedDays: this.isSelected,
      onDayClick: this.handleDayClick,
    };

    const className = clsx('Calendar', modifierClassName);

    // Note: Setting fromMonth on the dayPicker disables navigation to past months
    if (disablePastDates || disableAfterDate) {
      dayPickerProps.disabledDays = this.disablePastDates;
      dayPickerProps.fromMonth = time.toJSDate(this.today);
    }

    if (disableFutureDates) {
      dayPickerProps.disabledDays = this.disableFutureDates;
      dayPickerProps.toMonth = time.toJSDate(this.today);
    }

    return (
      <div className={className} data-testid="calendar-container">
        <div className="Calendar-content">
          <DayPicker
            {...dayPickerProps}
            ref={this.handleSetRef}
            captionElement={({ date }: { date: Date }) => (
              <YearMonthForm date={date} onChange={this.handleYearMonthChange} />
            )}
            navbarElement={CalendarNavBar}
          />
        </div>
      </div>
    );
  }
}

export default withContext(Calendar);
